import { defaultLeavePolicy } from '../data/hrDefaults'
import { HrEmployee, LeavePolicy, LeaveRequest, LeaveSnapshot, PayrollSettings } from '../types/hr'
import { overlapWeekdays, periodBounds } from './hrFormat'

/**
 * Monthly leave accrual ledger.
 *
 * Balances are derived on demand from stored records only — employment
 * commencement date, opening balance, approved leave requests and manual
 * adjustments. Nothing is "added" each month, so a month can never accrue
 * twice, re-running payroll cannot add extra days, and cancelling or
 * approving leave updates every later balance automatically.
 *
 * Available = Opening + Accrued − Taken + Adjustments
 */

export const leavePolicyOf = (settings: PayrollSettings): LeavePolicy => ({
  ...defaultLeavePolicy,
  ...(settings.leavePolicy ?? {}),
})

const round1 = (value: number) => Math.round(value * 10) / 10

const lastDayOfMonth = (year: number, month: number) => new Date(year, month, 0).getDate()

const nextPeriod = (period: string) => {
  const [year, month] = period.split('-').map(Number)
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`
}

const dayBefore = (iso: string) => {
  const date = new Date(`${iso}T00:00:00`)
  date.setDate(date.getDate() - 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/** Whole months of service completed between commencement and a date (anniversary-day based). */
export function completedMonths(startDate: string, at: string): number {
  if (!startDate || at < startDate) return 0
  const [y1, m1, d1] = startDate.split('-').map(Number)
  const [y2, m2, d2] = at.split('-').map(Number)
  let months = (y2 - y1) * 12 + (m2 - m1)
  // Day not yet reached this month — unless it's the month's last day (e.g. 31 Jan → 28 Feb).
  if (d2 < d1 && d2 < lastDayOfMonth(y2, m2)) months -= 1
  return Math.max(0, months)
}

export const isAccrualEligible = (employee: HrEmployee, policy: LeavePolicy) =>
  Boolean(employee.startDate) && policy.eligibleEmploymentTypes.includes(employee.employmentType)

/** One row per month from the start of tracking up to and including `untilPeriod`. */
export function leaveLedger(
  employee: HrEmployee,
  requests: LeaveRequest[],
  settings: PayrollSettings,
  untilPeriod: string,
): LeaveSnapshot[] {
  if (!employee.startDate) return []
  const policy = leavePolicyOf(settings)
  const leaveTypeName = settings.leaveTypes.find((type) => type.id === policy.leaveTypeId)?.name ?? 'Annual Leave'
  const opening = employee.leaveOpening
  const baseline = opening?.asOf && opening.asOf > employee.startDate ? opening.asOf : employee.startDate
  const firstPeriod = baseline.slice(0, 7)
  if (untilPeriod < firstPeriod) return []

  const eligible = isAccrualEligible(employee, policy)
  const serviceEnd = employee.status === 'Terminated' && employee.terminationDate ? employee.terminationDate : null
  const cap = policy.maxCarryForwardDays > 0 ? policy.maxCarryForwardDays : null
  const monthsAt = (date: string) =>
    completedMonths(employee.startDate, serviceEnd && date > serviceEnd ? serviceEnd : date)
  const accruedThrough = (date: string) => (eligible ? monthsAt(date) * policy.daysPerMonth : 0)

  const taken = requests.filter(
    (request) =>
      request.employeeId === employee.id &&
      request.leaveTypeId === policy.leaveTypeId &&
      request.status === 'Approved',
  )
  const adjustments = employee.leaveAdjustments ?? []

  const rows: LeaveSnapshot[] = []
  let balance = opening ? Number(opening.days) || 0 : 0
  let mark = accruedThrough(dayBefore(baseline))
  let totalAccrued = 0
  let totalTaken = 0

  for (let period = firstPeriod; period <= untilPeriod; period = nextPeriod(period)) {
    const { start, end } = periodBounds(period)
    const from = start < baseline ? baseline : start

    let forfeited = 0
    if (cap !== null && period !== firstPeriod && period.endsWith('-01') && balance > cap) {
      forfeited = round1(balance - cap)
      balance = cap
    }
    const openingBalance = round1(balance)

    const nextMark = accruedThrough(end)
    const accrued = round1(nextMark - mark)
    mark = nextMark

    const takenDays = taken.reduce(
      (sum, request) => sum + overlapWeekdays(request.startDate, request.endDate, from, end),
      0,
    )
    const adjusted = round1(
      adjustments
        .filter((item) => item.date >= from && item.date <= end)
        .reduce((sum, item) => sum + (Number(item.days) || 0), 0),
    )

    balance = round1(openingBalance + accrued - takenDays + adjusted)
    totalAccrued = round1(totalAccrued + accrued)
    totalTaken = round1(totalTaken + takenDays)

    rows.push({
      period,
      leaveTypeName,
      commencementDate: employee.startDate,
      eligible,
      daysPerMonth: policy.daysPerMonth,
      completedMonths: monthsAt(end),
      openingBalance,
      accrued,
      taken: takenDays,
      adjusted,
      forfeited,
      closingBalance: balance,
      totalAccrued,
      totalTaken,
    })
  }
  return rows
}

/** Leave position for a single payroll month (undefined before tracking starts). */
export function leaveSnapshot(
  employee: HrEmployee,
  requests: LeaveRequest[],
  settings: PayrollSettings,
  period: string,
): LeaveSnapshot | undefined {
  const rows = leaveLedger(employee, requests, settings, period)
  return rows[rows.length - 1]
}
